/**
 * AP-6 — konkrete Menübäume der 5 Toolbar-Menüs (Decoding/View/Filter/
 * Recorder/Help), 1:1 aus FEATURE_MATRIX.md §3. Häkchen werden bei jedem
 * Öffnen frisch aus `store` gelesen (siehe PopupMenu.ts).
 *
 * Zwei dokumentierte Korrekturen gegenüber FEATURE_MATRIX.md (Quelle direkt
 * nachgelesen, Einzelheiten in PROGRESS.md):
 * - "Dots"/"No Colors" liegen im Untermenü `Fade Speed`, nicht auf oberster
 *   Help-Ebene (CanMonitorApp.cpp:1239-1240).
 * - "Clear Filters" löscht nur Masken- und Range-Filter, siehe `store.clearFilters()`.
 */

import { createMenuButton, type MenuEntry } from '../ui/PopupMenu';
import { APP_THEMES, UI_SCALE_STEPS, themeDisplayName } from '../ui/theme';
import { RX_FADE_STEPS_MS } from './settings';
import type { AppStore } from './store';
import type { FilterCatalog } from '../worker/protocol';
import {
  createCheckListFilterDialog,
  createFilterMaskDialog,
  createFilterRangeDialog,
  type CheckListItem,
} from '../views/filters';
import { showAboutWindow } from '../views/Info';
import { showLicenseWindow } from '../views/License';

function pickTextFile(accept: string, onLoad: (fileName: string, text: string) => void, onCancel: () => void): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file === undefined) {
      onCancel();
      return;
    }
    void file.text().then((text) => onLoad(file.name, text));
  });
  input.addEventListener('cancel', () => onCancel());
  input.click();
}

// ---------------------------------------------------------------------------
// Decoding ⯆ (FEATURE_MATRIX.md §3.1)
// ---------------------------------------------------------------------------

export function createDecodingMenuButton(store: AppStore): HTMLButtonElement {
  const entries: MenuEntry[] = [];
  return createMenuButton('Decoding \u2bc7', () => {
    entries.length = 0;
    entries.push(
      {
        label: 'Load DBC...',
        onSelect: () => pickTextFile('.dbc', (name, text) => void store.loadDbc(name, text), () => store.dbcLoadCanceled()),
      },
      { label: 'DBC Browser', onSelect: () => store.openView('dbc-browser', false) },
      { label: 'DM1 Monitor...', onSelect: () => store.openDm1Monitor() },
      { label: 'Address Claim / NAME Monitor...', onSelect: () => store.openAddressClaim() },
      { label: 'Engine Simulator...', onSelect: () => store.openView('engine-simulator', false) },
      { kind: 'separator' },
      {
        label: 'Load EDS...',
        onSelect: () => pickTextFile('.eds', (name, text) => void store.loadEds(name, text), () => store.edsLoadCanceled()),
      },
      { label: 'EDS Browser', onSelect: () => store.openView('eds-browser', false) },
      { label: 'Node Scan...', onSelect: () => store.openNodeScan() },
      {
        label: 'Activate CANopen Decoding',
        checked: store.decodeFlags.canopenEnabled,
        onSelect: () => store.toggleCanopenDecoding(),
      },
      { kind: 'separator' },
      {
        label: 'Activate ISOBUS Decoding',
        checked: store.decodeFlags.isobusEnabled,
        onSelect: () => store.toggleIsobusDecoding(),
      },
      { label: 'ISOBUS Event Log...', onSelect: () => store.openIsobusEventLog() },
      { kind: 'separator' },
      { label: 'Reset Decoding', onSelect: () => void store.resetDecoding() },
    );
    return entries;
  });
}

// ---------------------------------------------------------------------------
// View ⯆ (FEATURE_MATRIX.md §3.2)
// ---------------------------------------------------------------------------

export function createViewMenuButton(store: AppStore): HTMLButtonElement {
  return createMenuButton('View \u2bc7', () => [
    { label: 'Static', checked: store.settings.viewMode === 'static', onSelect: () => store.setViewMode('static') },
    { label: 'Trace', checked: store.settings.viewMode === 'trace', onSelect: () => store.setViewMode('trace') },
    { kind: 'separator' },
    {
      label: 'Signal Trace / Plot...',
      onSelect: () => store.openView('signal-plot', store.settings.singleWindowMode),
    },
  ]);
}

// ---------------------------------------------------------------------------
// Filter ⯆ (FEATURE_MATRIX.md §3.3)
// ---------------------------------------------------------------------------

function buildErrorItems(catalog: FilterCatalog): CheckListItem[] {
  const items: CheckListItem[] = [];
  for (const [category, descriptions] of Object.entries(catalog.errorsByCategory)) {
    items.push({ key: category, label: category, bold: true });
    for (const description of descriptions) items.push({ key: description, label: `    ${description}` });
  }
  return items;
}

function buildSignalItems(catalog: FilterCatalog): CheckListItem[] {
  return catalog.signalNames.map((name) => ({ key: name, label: name }));
}

export function createFilterMenuButton(store: AppStore): HTMLButtonElement {
  const maskDialog = createFilterMaskDialog({
    onApply: (mask, maskId) => store.applyMaskFilter(mask, maskId),
    onInvalid: () => store.invalidMaskFilter(),
    onClear: () => store.clearContentFilter(),
  });
  const rangeDialog = createFilterRangeDialog({
    onApply: (min, max) => store.applyRangeFilter(min, max),
    onClear: () => store.clearRangeFilter(),
  });
  const errorDialog = createCheckListFilterDialog({
    title: 'Filter Error Frames',
    onApply: (keys) => store.applyErrorFrameFilter(keys),
    onClear: () => store.clearErrorFrameFilter(),
  });
  const signalDialog = createCheckListFilterDialog({
    title: 'Filter Signals',
    onApply: (keys) => store.applySignalFilter(keys),
    onClear: () => store.clearSignalFilter(),
  });
  store.on('catalog:updated', (catalog) => {
    errorDialog.setItems(buildErrorItems(catalog));
    signalDialog.setItems(buildSignalItems(catalog));
  });

  return createMenuButton('Filter \u2bc7', () => [
    { label: ' 11 Bit', checked: store.filter.show11Bit, onSelect: () => store.toggleShow11Bit() },
    { label: ' 29 Bit', checked: store.filter.show29Bit, onSelect: () => store.toggleShow29Bit() },
    { kind: 'separator' },
    {
      label: 'Filter Mask',
      checked: store.filter.contentFilterMode === 'mask',
      onSelect: () => maskDialog.panel.show(),
    },
    { label: 'Filter Range...', checked: store.filter.rangeEnabled, onSelect: () => rangeDialog.panel.show() },
    { kind: 'separator' },
    {
      label: 'Filter Error Frames...',
      onSelect: () => {
        errorDialog.setItems(buildErrorItems(store.catalog));
        errorDialog.panel.show();
      },
    },
    {
      label: 'Filter Signals...',
      onSelect: () => {
        signalDialog.setItems(buildSignalItems(store.catalog));
        signalDialog.panel.show();
      },
    },
    { kind: 'separator' },
    { label: 'Clear Filters', onSelect: () => store.clearFilters() },
  ]);
}

// ---------------------------------------------------------------------------
// Recorder (Tab-Button, FEATURE_MATRIX.md §3.4)
// ---------------------------------------------------------------------------

// CanMonitorApp.cpp:813 — Hintergrund der Recorder-Schaltflaeche waehrend
// Aufnahme/Wiedergabe, im 1-Hz-Takt von kBlinkTimerId ein- und ausgeblendet.
const RECORD_BLINK_BG = '#721010';
const REPLAY_BLINK_BG = '#105818';
const BLINK_INTERVAL_MS = 1000;

export function createRecorderMenuButton(store: AppStore): HTMLButtonElement {
  const button = createMenuButton('Recorder', () => [
    { label: store.recording ? 'Stop Record' : 'Start Record', onSelect: () => void store.toggleRecording() },
    { label: store.playing ? 'Stop Replay' : 'Start Replay', onSelect: () => void store.togglePlayback() },
    { kind: 'separator' },
    { label: ' Loop', checked: store.loopEnabled, onSelect: () => store.toggleLoop() },
  ]);

  let timer: number | undefined;
  let phase = false;

  const paint = (): void => {
    const color = store.recording ? RECORD_BLINK_BG : store.playing ? REPLAY_BLINK_BG : null;
    if (color === null || !phase) {
      button.classList.remove('cm-menu-btn--blink');
      button.style.removeProperty('--cm-blink-bg');
      return;
    }
    button.classList.add('cm-menu-btn--blink');
    button.style.setProperty('--cm-blink-bg', color);
  };

  store.on('recorder:changed', ({ recording, playing }) => {
    const active = recording || playing;
    if (active && timer === undefined) {
      phase = true; // sofort farbig, nicht erst nach der ersten Sekunde
      timer = window.setInterval(() => {
        phase = !phase;
        paint();
      }, BLINK_INTERVAL_MS);
    } else if (!active && timer !== undefined) {
      window.clearInterval(timer);
      timer = undefined;
      phase = false;
    }
    paint();
  });

  return button;
}

// ---------------------------------------------------------------------------
// Help ⯆ (FEATURE_MATRIX.md §3.5)
// ---------------------------------------------------------------------------

const FADE_LABELS: Readonly<Record<(typeof RX_FADE_STEPS_MS)[number], string>> = {
  0: 'Static (No Fade)',
  150: '1 (Fastest)',
  250: '2',
  400: '3',
  600: '4',
  900: '5 (Slowest)',
};

export function createHelpMenuButton(store: AppStore): HTMLButtonElement {
  return createMenuButton('Help', () => [
    {
      label: 'Scaling',
      submenu: UI_SCALE_STEPS.map((percent) => ({
        label: `${percent}%`,
        checked: store.settings.uiScalePercent === percent,
        onSelect: () => store.setUiScale(percent),
      })),
    },
    {
      label: 'Fade Speed',
      submenu: [
        ...[150, 250, 400, 600, 900].map((ms) => ({
          label: FADE_LABELS[ms as (typeof RX_FADE_STEPS_MS)[number]],
          checked: store.settings.rxFadeMs === ms,
          onSelect: () => store.setFadeMs(ms),
        })),
        { kind: 'separator' as const },
        { label: FADE_LABELS[0], checked: store.settings.rxFadeMs === 0, onSelect: () => store.setFadeMs(0) },
        { kind: 'separator' as const },
        {
          label: 'Dots',
          checked: store.settings.rxColorMode === 'dots',
          onSelect: () => store.setColorMode('dots'),
        },
        {
          label: 'No Colors',
          checked: store.settings.rxColorMode === 'no-colors',
          onSelect: () => store.setColorMode('no-colors'),
        },
      ],
    },
    {
      label: 'Theme',
      submenu: APP_THEMES.map((theme) => ({
        label: themeDisplayName(theme),
        checked: store.settings.theme === theme,
        onSelect: () => store.setTheme(theme),
      })),
    },
    { label: store.settings.singleWindowMode ? 'Separate Windows' : 'Single Window Mode', onSelect: () => store.toggleSingleWindowMode() },
    {
      label: 'Simulation Mode',
      checked: store.settings.simulationMode,
      onSelect: () => store.toggleSimulationMode(),
    },
    { kind: 'separator' },
    { label: 'About', onSelect: () => showAboutWindow() },
    { kind: 'separator' },
    { label: 'License...', onSelect: () => showLicenseWindow(store) },
  ]);
}
